import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Filter, SearchService } from '@docpath/linces-angular-components/common';
import { DpFormModule, DpHeaderModule, DpSearchConfig, DpSearchModule, DpSection, DpSectionIcon, HeaderAction, MoreMenuGroup, SearchBasicInfo, SearchMode, SearchOptionType, SearchRowInfo } from '@docpath/webcomponents';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PageHeaderActionsService } from 'src/app/shared/components/header/services/page-header-actions.service';

import { IndexService } from '../../models/index-service';
import { IndexServicesService } from '../../services/index-services.service';
import { IndexServicesTableService } from '../table/index-services-table.service';
import { IndexServicesHeaderService } from './index-services-header.service';


@Component({
  selector: 'app-index-services-header',
  templateUrl: './index-services-header.component.html',
  standalone: true,
  imports: [DpFormModule, DpHeaderModule, DpSearchModule]
})
export class IndexServicesHeaderComponent implements OnChanges, OnInit, OnDestroy {

  @Input() indexServices: IndexService[] = [];

  @Output() filtersChanged = new EventEmitter<Filter[]>();

  private readonly title = 'services-config.index-services.title';
  section = new DpSection({ name: 'index-services', rows: [], header: this.title, tooltip: this.title, icon: new DpSectionIcon(faGear) });
  showSearch = false;
  searchConfig = new DpSearchConfig({ resetOnClose: true, showMinimizeButton: true });

  rightActions: HeaderAction[] = [];
  leftActions: HeaderAction[] = [];
  moreMenuGroups: MoreMenuGroup[] = [];

  searchAvailableOptions: SearchRowInfo[] = [
    new SearchRowInfo(true, 'name', undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true),
    new SearchRowInfo(false, 'url', undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true)
  ];

  private filters: Filter[] = [];
  private indexServicesChangedSubscription?: Subscription;
  private translateSubscription?: Subscription;

  private readonly indexServicesHeaderService = inject(IndexServicesHeaderService);
  private readonly indexServicesTableService = inject(IndexServicesTableService);
  private readonly indexServicesService = inject(IndexServicesService);
  private readonly searchService = inject(SearchService);
  private readonly translateService = inject(TranslateService);
  private readonly pageHeaderActionsService = inject(PageHeaderActionsService);

  constructor() {
    this.rightActions = this.indexServicesHeaderService.getRightActions();
    const searchAction = this.indexServicesHeaderService.getSearchAction(this.rightActions);

    searchAction.action = () => {
      this.showSearch = !this.showSearch;
      this.updateSearchStyle();
    };
  }

  ngOnInit(): void {
    if (!this.indexServicesChangedSubscription) {
      this.indexServicesChangedSubscription = this.indexServicesService.indexServicesChanged.subscribe(() => {
        this.leftActions = [];
        this.moreMenuGroups = [];
      });
    }

    if (!this.translateSubscription) {
      this.translateSubscription = this.translateService.onLangChange.subscribe(() => {
        this.updateSearchStyle();
      });
    }
  }

  ngOnDestroy(): void {
    if (this.indexServicesChangedSubscription) {
      this.indexServicesChangedSubscription.unsubscribe();
    }

    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // eslint-disable-next-line dot-notation
    if (changes['indexServices']) {
      this.leftActions = this.indexServicesHeaderService.getLeftActions(this.indexServices);
      const moreMenuGroupsActions = this.indexServicesHeaderService.getMoreActions(this.indexServices);
      this.moreMenuGroups = moreMenuGroupsActions?.length > 0 ? [{ actions: moreMenuGroupsActions }] : [];
    }
  }

  isSearchVisible(): boolean {
    return this.showSearch;
  }

  clearSearch(): void {
    this.filters = [];
    this.filtersChanged.emit(this.filters);
    this.indexServicesTableService.loadIndexServices(this.filters, 1);
    this.leftActions = [];
    this.moreMenuGroups = [];
    this.updateSearchStyle(0);
  }

  sendSearch(currentFilters: Array<SearchBasicInfo>): void {
    this.filters = this.searchService.toFilters(currentFilters);
    this.filtersChanged.emit(this.filters);
    this.indexServicesTableService.loadIndexServices(this.filters, 1);
    this.leftActions = [];
    this.moreMenuGroups = [];
    this.updateSearchStyle();
  }

  hideSearch(): void {
    this.showSearch = false;
  }

  private updateSearchStyle(filtersCount?: number): void {
    const filterKeys = new Set(this.filters.map(filter => filter.key)),
      count = filtersCount ?? filterKeys.size;

    this.pageHeaderActionsService.updateSearchStyle(this.rightActions, count, 'search');
  }

}
